'use client';

import React, { useMemo } from 'react';
import usePopover from '~/hooks/usePopover';

import { ChevronDown } from 'lucide-react';

import Dropdown, { type DropdownProps } from './Dropdown';
import InputLabel from './InputLabel';
import { Checkbox } from '~/components/ui/checkbox';
import { Field } from '~/components/ui/field';
import { Separator } from '~/components/ui/separator';
import TruncatedText from '~/components/TruncatedText';
import { Badge } from '~/components/ui/badge';
import { cn } from '~/lib/utils';

export type Option<T = string> = { label: string; value: T };

export type MultiselectProps<T = string> = {
  label?: React.ReactNode;
  options: Array<Option<T>>;
  selected: T[];
  onChange?: (selected: T[]) => void;
  className?: string;
  readOnly?: boolean;
  tooltip?: React.ReactNode;
  popoverProps?: Partial<DropdownProps>;
  placeholder?: string;
};

function getSelectedLabels<T>(options: Array<Option<T>>, selected: T[]) {
  return selected
    .map((value) => options.find((option) => option.value === value)?.label)
    .filter((label): label is string => !!label);
}

export default function Multiselect<T = string>({
  options,
  selected,
  label,
  readOnly,
  onChange,
  className,
  tooltip,
  popoverProps,
  placeholder = 'Select values',
}: MultiselectProps<T>) {
  const { id, open, handleClose, handleOpen, anchorEl } = usePopover<HTMLButtonElement>();

  const selectedLabels = useMemo(
    () => getSelectedLabels(options, selected),
    [options, selected],
  );

  const visibleOptions = readOnly
    ? options.filter((option) => selected.includes(option.value))
    : options;

  const hideDropdown = !visibleOptions.length && readOnly;
  const allSelected = options.length > 0 && selected.length === options.length;

  const triggerLabel =
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length === 1
        ? selectedLabels[0]!
        : `${selectedLabels.length} selected`;

  const field = (
    <Field className="font-medium">
      {label ? <InputLabel label={label} tooltip={tooltip} /> : null}
      {readOnly ? (
        selectedLabels.length ? (
          <div className="flex flex-wrap gap-1.5">
            {selectedLabels.map((selectedLabel) => (
              <Badge key={selectedLabel} variant="secondary">
                {selectedLabel}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm font-normal">—</p>
        )
      ) : (
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={hideDropdown ? undefined : handleOpen}
          disabled={hideDropdown}
          className={cn(
            'border-input bg-background flex h-9 w-full items-center justify-between gap-2 rounded-md border px-3 py-0 text-sm leading-none shadow-xs transition-[color,box-shadow]',
            'focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-hidden',
            'disabled:cursor-not-allowed disabled:opacity-50',
            selectedLabels.length === 0 && 'text-muted-foreground font-normal',
            selectedLabels.length > 0 && 'text-foreground font-medium',
          )}
        >
          <TruncatedText className="min-w-0 text-left">{triggerLabel}</TruncatedText>
          {!hideDropdown ? (
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" strokeWidth={2} />
          ) : null}
        </button>
      )}
    </Field>
  );

  if (readOnly || hideDropdown) {
    return <div className={className}>{field}</div>;
  }

  return (
    <div className={className}>
      {field}
      <Dropdown
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={(event) => event.stopPropagation()}
        {...popoverProps}
      >
        <div className="w-64 p-2">
          {options.length > 1 ? (
            <>
              <label className="hover:bg-muted/30 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5">
                <Checkbox
                  className="size-4"
                  checked={allSelected}
                  onCheckedChange={(checked) => {
                    onChange?.(checked === true ? options.map((option) => option.value) : []);
                  }}
                />
                <span className="text-foreground text-sm font-medium">Select all</span>
              </label>
              <Separator className="my-1" />
            </>
          ) : null}
          <div className="max-h-48 flex flex-col gap-0.5 overflow-y-auto" role="listbox" aria-label="Values">
            {visibleOptions.map((option) => {
              const checked = selected.includes(option.value);

              return (
                <label
                  key={String(option.value)}
                  className="hover:bg-muted/30 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5"
                >
                  <Checkbox
                    className="size-4"
                    checked={checked}
                    onCheckedChange={(value) => {
                      const newSelected =
                        value === true
                          ? [...selected, option.value]
                          : selected.filter((item) => item !== option.value);
                      onChange?.(newSelected);
                    }}
                  />
                  <span className="text-foreground text-sm">{option.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      </Dropdown>
    </div>
  );
}
